import React from 'react';
import clsx from 'clsx';
import type { AppLayoutProps, ViewType } from '../../types/components';
import styles from './AppLayout.module.css';

type AdminViewKey = 'bam-database' | 'data-quality' | 'categories-quality' | 'reports' | 'quality-check' | 'perfect-match';

const normalizeAdminView = (view: ViewType): AdminViewKey => {
  switch (view) {
    case 'bam-database':
      return 'bam-database';
    case 'categories-quality':
    case 'data-quality':
    case 'reports':
      return view;
    case 'search-discover':
      return 'bam-database';
    case 'perfect-match':
    case 'match':
      return 'perfect-match';
    case 'operations':
    case 'manual-entry':
    case 'enrichment-verification':
    case 'quality-check':
      return 'quality-check';
    default:
      return 'data-quality';
  }
};

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  currentView = 'data-quality',
  onViewChange,
  layoutMode = 'admin'
}) => {
  const handleLogoClick = () => {
    if (onViewChange) {
      onViewChange('homepage');
      return;
    }
    window.location.hash = '';
  };

  const navigationGroups = layoutMode === 'client'
    ? [{
        heading: 'Client Portal',
        items: [
          { key: 'sell-books-start', label: 'Sell More Books', enabled: true },
          { key: 'build-business-start', label: 'Build Business', enabled: true },
          { key: 'industry-leader-start', label: 'Industry Leader', enabled: true },
          { key: 'video', label: 'Video Library', enabled: true }
        ]
      }]
    : [
        {
          heading: 'Data',
          items: [
            { key: 'data-quality', label: 'Programs', enabled: true },
            { key: 'categories-quality', label: 'Categories', enabled: true },
            { key: 'reports', label: 'Reports', enabled: true },
          ]
        },
        {
          heading: 'Workflows',
          items: [
            { key: 'quality-check', label: 'Quality Check', enabled: true },
            { key: 'perfect-match', label: 'Perfect Match', enabled: true },
          ]
        }
      ];

  const effectiveView = layoutMode === 'admin'
    ? normalizeAdminView(currentView)
    : currentView;

  const layoutMaxWidth = '1200px';

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.inner} style={{ maxWidth: layoutMaxWidth }}>
          <div className={styles.brand}>
            <button
              type="button"
              className={styles.logoButton}
              onClick={handleLogoClick}
              aria-label="Return to homepage"
            >
              <img
                src="/BAM-logo-embedded.svg"
                alt="Book Award Mastery logo"
                className={styles.logo}
              />
            </button>
            <h1 className={styles.title}>Book Award Mastery Database</h1>
          </div>
          <nav className={styles.navWrapper} aria-label="Primary navigation">
            {navigationGroups.map((group) => (
              <div key={group.heading} className={styles.navGroup}>
                <span className={styles.groupLabel}>{group.heading}</span>
                <div className={styles.navItems}>
                  {group.items.map((item) => {
                    const isEnabled = item.enabled !== false;
                    const normalizedItemKey = layoutMode === 'admin'
                      ? normalizeAdminView(item.key as ViewType)
                      : item.key;
                    const isActive = layoutMode === 'admin'
                      ? effectiveView === normalizedItemKey
                      : currentView === item.key;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={clsx(
                          styles.navButton,
                          isActive && styles.navButtonActive,
                          !isEnabled && styles.navButtonDisabled
                        )}
                        disabled={!isEnabled}
                        onClick={() => {
                          if (isEnabled && onViewChange) {
                            onViewChange(item.key as ViewType);
                          }
                        }}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </header>
      <main className={styles.main} style={{ maxWidth: layoutMaxWidth }}>
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
